import type { RoundingRuleValueModel } from '@/shared/generated'

import { useTranslation } from 'react-i18next'

import ListGroup, { ListGroupItem } from '@/shared/components/ListGroup'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Separator } from '@/shared/components/ui/separator'
import { formatNumber } from '@/shared/tools/locale'

export const RoundingRulesView = ({
	settings,
}: {
	settings: RoundingRuleValueModel[]
}) => {
	const { t } = useTranslation()

	return (
		<Card className='shadow-none'>
			<CardHeader>
				<CardTitle>{t('settings.rounding.long')}</CardTitle>
			</CardHeader>
			<CardContent className='pt-0'>
				<ListGroup className='flex flex-col gap-2'>
					{settings.map((setting: RoundingRuleValueModel, index) => {
						const isLastItem = index === settings.length - 1
						const max = settings[index + 1]
							? ((settings[index + 1].min_price || 0) - 0.01).toFixed(2)
							: '∞'
						const lastDigits = setting.params?.last_digits
						const params = setting.params

						return (
							<ListGroupItem
								key={setting.strategy}
								className='flex flex-col gap-2'
							>
								<div className='flex flex-col gap-4'>
									<span className='font-semibold'>{`${t('general_range')} ${index + 1}`}</span>
									<DescriptionList className='flex gap-4'>
										<DescriptionGroup className='flex w-48 flex-col'>
											<DescriptionTerm>{t('general_range')}</DescriptionTerm>
											<DescriptionDetail>
												<span className='font-semibold'>
													{setting.min_price} - {max}
												</span>
											</DescriptionDetail>
										</DescriptionGroup>
										<DescriptionGroup className='flex w-56 flex-col'>
											<DescriptionTerm>{t('strategy')}</DescriptionTerm>
											<DescriptionDetail>
												<span className='font-semibold'>
													{t(`settings_rounding_${setting.strategy}_title`, {
														defaultValue: setting.strategy || '',
													})}
												</span>
											</DescriptionDetail>
										</DescriptionGroup>
									</DescriptionList>

									{!!params && (
										<div className='flex flex-col gap-4'>
											<DescriptionList className='flex flex-col'>
												{!!setting.params?.last_digits && (
													<DescriptionGroup className='flex flex-col'>
														<DescriptionTerm>
															{t('settings_rounding_last_digits_title')}
														</DescriptionTerm>
														<DescriptionDetail className='flex flex-wrap gap-1'>
															{lastDigits.map((item: string) => (
																<span
																	key={item}
																	className='h-5 min-w-5 rounded-2xl border px-2 py-0.5 text-xs font-medium'
																>
																	{formatNumber(Number(item))}
																</span>
															))}
														</DescriptionDetail>
													</DescriptionGroup>
												)}
												{setting.strategy === 'factor' &&
													!!setting.params?.factor && (
														<DescriptionGroup className='flex flex-col'>
															<DescriptionTerm>
																{t('settings_rounding_factor_factor_title', {
																	defaultValue: 'Factor',
																})}
															</DescriptionTerm>
															<DescriptionDetail>
																<span className='font-semibold'>
																	{formatNumber(Number(setting.params.factor))}
																</span>
															</DescriptionDetail>
														</DescriptionGroup>
													)}
												{!!setting.params?.difference && (
													<DescriptionGroup className='flex flex-col'>
														<DescriptionTerm>
															{t('settings_rounding_factor_difference_title')}
														</DescriptionTerm>
														<DescriptionDetail>
															<span className='font-semibold'>
																{formatNumber(
																	Number(setting.params.difference)
																)}
															</span>
														</DescriptionDetail>
													</DescriptionGroup>
												)}
												{!!setting.params?.rounding_mode && (
													<DescriptionGroup className='flex flex-col'>
														<DescriptionTerm>
															{t(
																'settings_rounding_factor_rounding_mode_title'
															)}
														</DescriptionTerm>
														<DescriptionDetail>
															<span className='font-semibold'>
																{t(
																	`root_rounding_mode_${setting.params.rounding_mode}`,
																	{ defaultValue: setting.params.rounding_mode }
																)}
															</span>
														</DescriptionDetail>
													</DescriptionGroup>
												)}
											</DescriptionList>
										</div>
									)}
								</div>
								{!isLastItem && <Separator />}
							</ListGroupItem>
						)
					})}
				</ListGroup>
			</CardContent>
		</Card>
	)
}
